import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";

/**
 * Workflow step: transform `rexel_subchain` raw rows into
 * `im_product_supercedes` domain rows.
 *
 * Stage 2 of the SUBCHAIN ingest pipeline. Runs after `master_data_bulk_insert`
 * has populated `rexel_subchain`. For each row in the latest batch:
 *   - Resolves old SKU → `im_products_v2.id`
 *   - Resolves new SKU → `im_products_v2.id`
 *   - Upserts into `im_product_supercedes` keyed on the old/new product pair
 *
 * Scoping: `updatedAfter` (typically wired from upstream `startedAt`) limits
 * the rexel_subchain rows considered to those touched by the current run.
 */
export const transformSubchainSupercedesStep = defineStepConfig({
  type: "transform_subchain_supercedes",
  name: "Transform SUBCHAIN → Product Supercedes",
  description:
    "Reads rexel_subchain rows updated by the current run, resolves old/new SKU pairs, and upserts into im_product_supercedes.",
  category: "Transformation",
  libraryModule: "Procurement",
  inputSchema: z.object({
    updatedAfter: z
      .string()
      .optional()
      .describe(
        "ISO timestamp — only process rows updated after this time. " +
          "Typically wired from the upstream master_data_bulk_insert's startedAt output.",
      ),
  }),
  outputSchema: z.object({
    totalRows: z
      .number()
      .describe("Total number of rexel_subchain rows processed"),
    created: z
      .number()
      .describe("Number of new im_product_supercedes rows created"),
    updated: z
      .number()
      .describe("Number of existing im_product_supercedes rows updated"),
    skippedUnresolved: z
      .number()
      .describe(
        "Number of rows skipped because the old or new SKU could not be resolved in im_products_v2",
      ),
    updatedAfter: z
      .string()
      .optional()
      .describe(
        "Pass-through of the input timestamp so downstream steps can scope to the current run.",
      ),
  }),
  configSchema: z.object({
    batchSize: z
      .number()
      .min(1)
      .max(5000)
      .prefault(500)
      .describe("Number of rows to process per batch"),
  }),
  emits: [] as const,
  execution: {
    startToCloseTimeout: "30 minutes",
    retry: { maximumAttempts: 2 },
  },
});
